import { Component, EventEmitter, Inject, Input, Output, ViewEncapsulation } from '@angular/core';
import {
  Article,
  ArticleFacetedSearchQuery,
  ArticleFacetedSearchService,
  CartHeader,
  CartService,
  Facet,
  FilterService,
  Product,
  ProductPageViewFilterService,
  SortByType,
  SteelCartItem,
  SteelCreateCartItemInterface,
  ViewModeType,
} from '@lobos/library';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ArticleAccessoriesDialogService } from '../../../../catalog-services/accessories/article-accessories-dialog.service';
import { BreakpointService } from '../../../../../../services/ui/breakpoint.service';
import { WeylandSteelArticle } from '../../../../../../interfaces/steel-article.model';
import { DOCUMENT, ViewportScroller } from '@angular/common';
import { PaginationInstance } from 'ngx-pagination';
import { combineLatest, Observable } from 'rxjs';

// @TrackClass<ProductArticleListComponent, ViewItemListEvent>({
//   initializer: 'ngOnChanges',
//   provide: (self: ProductArticleListComponent) =>
//     of(self.articles).pipe(
//       filter((articles: Article[]) => articles.length > 0 && !!self.product),
//       take(1),
//       map((articles: Article[]) => ({
//         name: 'view_item_list',
//         payload: {
//           ecommerce: {
//             item_list_id: self.product.lngParentGroup.toString(),
//             item_list_name: self.product.sTitle,
//             items: articles!.map((article: Article) => GoogleTagManagerHelper.articleToItem(article)),
//           },
//         },
//       })),
//     ),
// })
@UntilDestroy()
@Component({
  selector: 'app-product-article-list',
  templateUrl: './product-article-list.component.html',
  styleUrls: ['./product-article-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProductArticleListComponent {
  @Input() product!: Product;
  @Input() articles!: WeylandSteelArticle[];
  @Input() totalHits!: number | null;
  @Input() facets!: Facet[] | null;
  @Output() alternativeArticleSelected: EventEmitter<Article> = new EventEmitter<Article>();

  private quantity = 1;
  public viewMode = 'list';
  public isTablet = true;
  config: PaginationInstance | undefined;
  sortBy$: Observable<string> = this.articleFacetedSearchQuery.sortBy$;

  sortOptions = [
    { key: 'sku', value: 'catalog.product-article-list.sort-ascending' },
    { key: '-sku', value: 'catalog.product-article-list.sort-descending' },
    { key: 'title', value: 'catalog.product-article-list.sort-description' },
  ];

  constructor(
    private breakpointService: BreakpointService,
    private filterService: FilterService,
    private cartService: CartService<CartHeader, SteelCartItem, SteelCreateCartItemInterface>,
    private translocoService: TranslocoService,
    private dialogService: ArticleAccessoriesDialogService,
    private articleFacetedSearchQuery: ArticleFacetedSearchQuery,
    private articleFacetedSearchService: ArticleFacetedSearchService,
    private productPageViewFilterService: ProductPageViewFilterService,
    private scroller: ViewportScroller,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.breakpointService.tablet.pipe(untilDestroyed(this)).subscribe((isTablet: boolean) => {
      this.isTablet = isTablet;
      if (isTablet) {
        this.viewMode = 'grid';
      } else {
        this.viewMode = 'list';
      }
    });
    combineLatest([this.articleFacetedSearchQuery.pageSize$, this.articleFacetedSearchQuery.pageIndex$])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([pageSize, currentPage]: [number, number]) =>
          (this.config = {
            ...this.config,
            id: 'custom',
            itemsPerPage: pageSize,
            currentPage: currentPage,
          }),
      );

    this.articleFacetedSearchService.addSortMapping('sku', [{ 'sArticleID.keyword': 'asc' }]);
    this.articleFacetedSearchService.addSortMapping('-sku', [{ 'sArticleID.keyword': 'desc' }]);
    this.articleFacetedSearchService.setDefaultSort('sku');
    this.productPageViewFilterService.updateDefaults({ sortBy: 'sku', pageSize: 16 });
  }

  onQuantityChange(quantity: number) {
    this.quantity = quantity;
  }

  changeViewMode(viewMode: ViewModeType) {
    this.viewMode = viewMode;
  }

  selectAlternativeArticle(article: WeylandSteelArticle) {
    this.dialogService.open(article);
  }

  changeCurrentPage(page: number) {
    this.productPageViewFilterService.updateCurrentPage(page - 1);
    this.scrollToAnchor();
  }

  sortByChange(sortBy: SortByType) {
    this.productPageViewFilterService.updateSort(sortBy);
    this.productPageViewFilterService.updateCurrentPage(0);
  }

  resetPagination() {
    this.productPageViewFilterService.updateCurrentPage(0);
  }

  scrollToAnchor() {
    this.scroller.setOffset([0, 300]);
    this.scroller.scrollToAnchor('topOfArticleList');
  }
}
